import type { Document } from "@/api/models"
import type { DeepActivityEvent } from "@/stores/ai-explorer-store"

import {
  documentsFromSourceCards,
  mergeSourceCards,
  type SourceCardData,
  sourceCardsFromSavedDocuments,
} from "./source-card-utils"

const TOOL_TO_TYPE_MAP: Record<string, SourceCardData["type"]> = {
  library_search: "library",
  file_search: "doc",
  doc_internal_search: "doc",
  doc_content_reader: "doc",
  web_search: "web",
  web_scrape: "web",
  microsoft_365_search: "m365",
}

function toolBucket(tool: string): SourceCardData["type"] {
  return TOOL_TO_TYPE_MAP[tool] ?? "other"
}

function buildSourceId(
  toolEvent: DeepActivityEvent,
  preview: Record<string, unknown>,
  index: number
): string {
  const lib = typeof preview["library"] === "string" ? (preview["library"] as string) : ""
  const file = typeof preview["sourcefile"] === "string" ? (preview["sourcefile"] as string) : ""
  if (lib && file) return `${lib}:${file}`
  const url = typeof preview["url"] === "string" ? (preview["url"] as string) : ""
  if (url) return url
  const id = typeof preview["id"] === "string" ? (preview["id"] as string) : ""
  if (id) return id
  const title = typeof preview["title"] === "string" ? (preview["title"] as string) : ""
  return `${toolEvent.id}:${index}:${title}`
}

function previewString(preview: Record<string, unknown>, key: string): string {
  const value = preview[key]
  return typeof value === "string" && value ? value : ""
}

function sourceCardTitle(preview: Record<string, unknown>): string {
  return (
    previewString(preview, "filth_citation_alias") ||
    previewString(preview, "title") ||
    previewString(preview, "sourcefile") ||
    "Source"
  )
}

function buildMatchKeys(preview: Record<string, unknown>): string[] {
  const keys: string[] = []
  const lib = typeof preview["library"] === "string" ? preview["library"] : ""
  const file = typeof preview["sourcefile"] === "string" ? preview["sourcefile"] : ""
  // A FILTH document is cited by its alias, so that is what a deliverable
  // citation contains; `sourcefile` is the opaque locator and appears in no
  // citation the model writes. Both are kept, because a session that predates
  // the alias still has handle-shaped citations in its stored deliverable.
  const alias =
    typeof preview["filth_citation_alias"] === "string" ? preview["filth_citation_alias"] : ""
  if (lib && alias) keys.push(`${lib}:${alias}`)
  if (alias) keys.push(alias as string)
  if (lib && file) {
    keys.push(`${lib}:${file}`)
  }
  if (file) keys.push(file as string)
  const url = typeof preview["url"] === "string" ? preview["url"] : ""
  if (url) keys.push(url as string)
  const id = typeof preview["id"] === "string" ? preview["id"] : ""
  if (id) keys.push(id as string)
  const title = typeof preview["title"] === "string" ? preview["title"] : ""
  if (title && !keys.includes(title as string)) keys.push(title as string)
  return keys.filter(Boolean) as string[]
}

/**
 * Collect SourceCardData from the activity log. Deduplicates by match key,
 * aggregating hit counts from multiple tool calls against the same source.
 */
export function collectSourceCards(activityLog: DeepActivityEvent[]): SourceCardData[] {
  const byId = new Map<string, SourceCardData>()
  for (const event of activityLog) {
    if (event.event !== "tool_end" || !event.result_summary) continue
    const rawPreviews = event.result_summary["preview_items"]
    const previews = Array.isArray(rawPreviews) ? rawPreviews : []
    previews.forEach((rawPreview, index) => {
      if (!rawPreview || typeof rawPreview !== "object") return
      const preview = rawPreview as Record<string, unknown>
      const id = buildSourceId(event, preview, index)
      const type = toolBucket(event.tool)
      const title = sourceCardTitle(preview)
      const snippet =
        typeof preview["snippet"] === "string" ? (preview["snippet"] as string) : undefined
      const existing = byId.get(id)
      if (existing) {
        if (snippet && (!existing.snippet || snippet.length > existing.snippet.length)) {
          existing.snippet = snippet
        }
        existing.citationCount = (existing.citationCount ?? 1) + 1
        return
      }
      byId.set(id, {
        id,
        title,
        snippet,
        type,
        tool: event.tool,
        timestamp: event.timestamp,
        library:
          typeof preview["library"] === "string" ? (preview["library"] as string) : undefined,
        sourcefile:
          typeof preview["sourcefile"] === "string" ? (preview["sourcefile"] as string) : undefined,
        sourcepage:
          typeof preview["sourcepage"] === "string" ? (preview["sourcepage"] as string) : undefined,
        url: typeof preview["url"] === "string" ? (preview["url"] as string) : undefined,
        filthContentHandle:
          typeof preview["filth_content_handle"] === "string"
            ? (preview["filth_content_handle"] as string)
            : undefined,
        filthCitationAlias:
          typeof preview["filth_citation_alias"] === "string"
            ? (preview["filth_citation_alias"] as string)
            : undefined,
        matchKeys: buildMatchKeys(preview),
        citationCount: 1,
        retrievedBy: event.source,
        dispatchId: event.dispatch_id ?? null,
      })
    })
  }
  return Array.from(byId.values())
}

/**
 * Group already-collected source cards by their dispatching lane (B6).
 * The lane key matches `buildLanes` in worker-lanes-utils — `"deep_explorer"`
 * for any source the orchestrator pulled directly, else
 * `${retrievedBy}:${dispatchId ?? "unknown"}`. WorkerLanes uses this so a
 * given lane can render only the sources that lane actually fetched, instead
 * of the panel-wide flat ribbon.
 */
export function groupSourceCardsByLane(sources: SourceCardData[]): Map<string, SourceCardData[]> {
  const byLane = new Map<string, SourceCardData[]>()
  for (const source of sources) {
    const retrievedBy = source.retrievedBy ?? "deep_explorer"
    const key =
      retrievedBy === "deep_explorer"
        ? "deep_explorer"
        : `${retrievedBy}:${source.dispatchId ?? "unknown"}`
    const existing = byLane.get(key)
    if (existing) existing.push(source)
    else byLane.set(key, [source])
  }
  return byLane
}

/** One saved assistant answer. Activity and documents are that turn only. */
export interface AnswerCitationInputs {
  activityLog: DeepActivityEvent[]
  documents?: [string, Document][]
  /**
   * Persisted workspace artifacts for this turn. A `document` counts as
   * `/deliverable.md`; a `plan` counts as `/plan.md`.
   */
  artifacts?: ReadonlyArray<{ type?: string | null }>
}

export interface DeliverableCitationScope {
  cards: SourceCardData[]
  documents: [string, Document][]
}

const ARCHIVED_DELIVERABLE_RE = /^\/deliverable-(\d+)\.md$/
const SAVED_ACTIVITY_EVENTS = new Set(["tool_start", "tool_end", "status"])

/** One index per turn that wrote one of these, matching `_next_archive_index`. */
const ARCHIVABLE_PATHS = new Set([
  "/plan.md",
  "/deliverable.md",
  "/followups.json",
  "/quality_review.json",
  "/verifications.json",
])

const WRITE_PATH_KEYS = ["path", "file_path", "filepath"]

function emptyCitationScope(): DeliverableCitationScope {
  return { cards: [], documents: [] }
}

function citationActivityKey(event: { id?: string; event?: string }): string | null {
  if (!event.id) return null
  return `${event.id}:${event.event ?? ""}`
}

/**
 * Map a saved message's `deep_activity_events` into the activity-log shape
 * `collectSourceCards` reads. Truncation sentinels carry no previews.
 */
export function citationActivityFromSaved(
  events:
    | ReadonlyArray<{
        id: string
        event: string
        tool?: string
        source?: string
        dispatch_id?: string | null
        timestamp?: string
        input_summary?: Record<string, unknown>
        result_summary?: Record<string, unknown>
      }>
    | undefined
): DeepActivityEvent[] {
  if (!events?.length) return []
  const mapped: DeepActivityEvent[] = []
  for (const raw of events) {
    if (!SAVED_ACTIVITY_EVENTS.has(raw.event)) continue
    mapped.push({
      id: raw.id,
      event: raw.event as DeepActivityEvent["event"],
      tool: raw.tool ?? "",
      source: raw.source ?? "deep_explorer",
      dispatch_id: raw.dispatch_id ?? null,
      timestamp: raw.timestamp ?? "",
      input_summary: raw.input_summary,
      result_summary: raw.result_summary,
    })
  }
  return mapped
}

/** Cards and delivered documents for a single answer. Never mixes turns. */
export function scopeForAnswer(answer: AnswerCitationInputs): DeliverableCitationScope {
  const cards = mergeSourceCards(
    collectSourceCards(answer.activityLog),
    sourceCardsFromSavedDocuments(answer.documents)
  )
  return { cards, documents: documentsFromSourceCards(cards) }
}

/**
 * Activity events that belong to the turn still being written.
 *
 * Saved answers claim their event ids. The tail after the last claimed id is
 * the live turn. When saved ids exist but none of them are in this log, the
 * client cap dropped the earlier turns and the log that remains is the live
 * turn. A log with no ids to claim is the live turn only when nothing has
 * been saved yet; a legacy log is not a turn boundary.
 */
function liveActivityForCurrentTurn(
  activityLog: DeepActivityEvent[],
  answers: AnswerCitationInputs[]
): DeepActivityEvent[] {
  const claimed = new Set<string>()
  for (const answer of answers) {
    for (const event of answer.activityLog) {
      const key = citationActivityKey(event)
      if (key) claimed.add(key)
    }
  }
  let lastClaimed = -1
  if (claimed.size > 0) {
    for (let index = 0; index < activityLog.length; index++) {
      const key = citationActivityKey(activityLog[index])
      if (key && claimed.has(key)) lastClaimed = index
    }
  }
  if (lastClaimed >= 0) {
    return activityLog.slice(lastClaimed + 1).filter((event) => {
      const key = citationActivityKey(event)
      return key === null || !claimed.has(key)
    })
  }
  if (claimed.size === 0) return answers.length === 0 ? activityLog : []
  return activityLog
}

function writeFileArchivePath(event: DeepActivityEvent): string | null {
  if (event.tool !== "write_file") return null
  if (event.event !== "tool_start" && event.event !== "tool_end") return null
  const summary = event.input_summary
  if (!summary) return null
  for (const key of WRITE_PATH_KEYS) {
    const value = summary[key]
    if (typeof value === "string" && ARCHIVABLE_PATHS.has(value)) return value
  }
  return null
}

function artifactArchivePath(artifact: { type?: string | null } | null | undefined): string | null {
  if (artifact?.type === "document") return "/deliverable.md"
  if (artifact?.type === "plan") return "/plan.md"
  return null
}

/** A turn consumes one archive index when it wrote any archivable path. */
function answerArchiveSignals(answer: AnswerCitationInputs): {
  consumes: boolean
  wroteDeliverable: boolean
} {
  let consumes = false
  let wroteDeliverable = false
  const note = (path: string | null) => {
    if (!path) return
    consumes = true
    if (path === "/deliverable.md") wroteDeliverable = true
  }
  for (const event of answer.activityLog) note(writeFileArchivePath(event))
  for (const artifact of answer.artifacts ?? []) note(artifactArchivePath(artifact))
  return { consumes, wroteDeliverable }
}

function answerHasCardsOrDocuments(answer: AnswerCitationInputs): boolean {
  if ((answer.documents?.length ?? 0) > 0) return true
  return collectSourceCards(answer.activityLog).length > 0
}

/**
 * Document list for a deliverable citation click.
 *
 * `/deliverable-N.md` is the Nth turn (1-based) that wrote an archivable
 * file. A saved cancel that wrote none of those does not consume an index.
 * If that turn did not write a deliverable, the scope is empty — not a
 * neighbor's documents. `/deliverable.md` is the live tail only when that
 * tail has source cards or unsaved documents. A `write_file` / `status` /
 * `write_todos` with no sources falls back to the latest answer that has
 * cards or documents. One answer only; aliases are not unique across turns.
 */
export function selectDeliverableCitationScope(input: {
  path: string | null
  answers: AnswerCitationInputs[]
  activityLog: DeepActivityEvent[]
  liveDocuments?: [string, Document][]
}): DeliverableCitationScope {
  const archived = input.path ? ARCHIVED_DELIVERABLE_RE.exec(input.path) : null
  if (archived) {
    const nth = Number(archived[1])
    if (!Number.isInteger(nth) || nth < 1) return emptyCitationScope()
    let seen = 0
    for (const answer of input.answers) {
      const signals = answerArchiveSignals(answer)
      if (!signals.consumes) continue
      seen += 1
      if (seen !== nth) continue
      if (!signals.wroteDeliverable) return emptyCitationScope()
      return scopeForAnswer(answer)
    }
    return emptyCitationScope()
  }
  const liveActivity = liveActivityForCurrentTurn(input.activityLog, input.answers)
  if (input.path === "/deliverable.md") {
    const liveScope = scopeForAnswer({
      activityLog: liveActivity,
      documents: input.liveDocuments,
    })
    if (liveScope.cards.length > 0 || (input.liveDocuments?.length ?? 0) > 0) {
      return liveScope
    }
    for (let index = input.answers.length - 1; index >= 0; index -= 1) {
      const answer = input.answers[index]
      if (answerHasCardsOrDocuments(answer)) return scopeForAnswer(answer)
    }
    return emptyCitationScope()
  }
  if (liveActivity.length > 0 || (input.liveDocuments?.length ?? 0) > 0) {
    return scopeForAnswer({ activityLog: liveActivity, documents: input.liveDocuments })
  }
  if (input.answers.length === 0) return emptyCitationScope()
  return scopeForAnswer(input.answers[input.answers.length - 1])
}
